import React, { useState } from 'react';

// Types
import { User } from 'Shared/constants/types';
import { Workspace } from 'Shared/types/Workspace.type';

// Components
import { WpButton } from 'Wpds/components/button';
import { WpDataTableV2, SortDirections } from 'Wpds/components/table-v2';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WorkspaceMemberItem from './WorkspaceMemberItem';
import WorkspaceMembersInvite from './WorkspaceMembersInvite';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import WorkspaceMemberItemRemove from './WorkspaceMemberItemRemove';

// Styles
import './WorkspaceDetailsMembers.scss';

enum Views {
  members = 'members',
  invite = 'invite',
}

type Props = {
  users: Array<User>;
  workspace: Workspace;
  view?: Views;
};

const WorkspaceDetailsMembers = ({ users, workspace, view = Views.members }: Props) => {
  const [currentView, setCurrentView] = useState(view);
  const [textSearchInput, setTextSearchInput] = useState('');

  const { isGroup } = workspace;

  const filteredUsers = users.filter(
    user =>
      user.fullName.toLocaleLowerCase().includes(textSearchInput.toLocaleLowerCase()) ||
      user.email.toLocaleLowerCase().includes(textSearchInput.toLocaleLowerCase())
  );

  const handleSetView = (view: Views) => {
    setTextSearchInput('');
    setCurrentView(view);
  };

  const showEmptyState = filteredUsers.length === 0;
  const showOnClear = textSearchInput !== '';

  const columns = [
    {
      label: 'Name',
      selector: row => row.fullName,
      component: ({ row }) => <WorkspaceMemberItem user={row} />,
      maxWidth: '25rem',
      sort: SortDirections.ASC,
    },
    {
      label: 'Actions',
      rightAlign: true,
      hideLabel: true,
      component: ({ row }) => <WorkspaceMemberItemRemove user={row} workspace={workspace} />,
      isSortable: false,
    },
  ];

  const handleOnClear = () => {
    setTextSearchInput('');
  };

  if (!isGroup) return null;
  const baseClassName = 'WorkspaceDetailsMembers';

  return (
    <div className={baseClassName}>
      {currentView === Views.members && (
        <>
          <div className={`${baseClassName}__toolbar`}>
            <div className={`${baseClassName}__toolbar__search`}>
              <WpTextField
                id="members-search-input"
                label="Find Members"
                placeholder="Find Members"
                labelHidden
                prefixIcon="search"
                value={textSearchInput}
                onChange={setTextSearchInput}
                onClear={showOnClear && handleOnClear}
              />
            </div>
          </div>
          {showEmptyState ? (
            <EmptyStateContainer
              title=""
              subTitle="No matches found"
              button={
                <WpButton variant="secondary" color="gray" onClick={() => handleSetView(Views.invite)}>
                  Add People
                </WpButton>
              }
            />
          ) : (
            <div className={`${baseClassName}__table`}>
              {
                <div
                  className={`${baseClassName}__add-bar`}
                  role="button"
                  onClick={() => handleSetView(Views.invite)}
                  data-test="WorkspaceDetailModal-invite-button"
                >
                  <div className={`${baseClassName}__add-bar__icon`}>
                    <WpIcon name="user-plus" size="lg" />
                  </div>
                  <p className={`${baseClassName}__add-bar__label`}>Invite People</p>
                </div>
              }
              <WpDataTableV2
                tableId="membersTable"
                data={filteredUsers}
                columns={columns}
                showInContainer={false}
                showHeader={false}
                showBorder={false}
                slimRow
              />
            </div>
          )}
        </>
      )}
      {currentView === Views.invite && (
        <WorkspaceMembersInvite handleBackNavigation={() => handleSetView(Views.members)} workspace={workspace} />
      )}
    </div>
  );
};

export default WorkspaceDetailsMembers;
